import { useCallback, useMemo, useState, useEffect } from "react";
import CommunityMap from "../components/CommunityMap";
import { communities, communityFilters, guilds } from "../data";

const defaultFilters = {
  guild: "all",
  platform: "all",
  region: "all"
};

const mapStats = [
  { label: "Guild dossiers", value: guilds.length },
  { label: "Platforms tracked", value: communityFilters.platforms.length },
  { label: "Regions covered", value: communityFilters.regions.length }
];

const GlobalMap = () => {
  const [filters, setFilters] = useState(() => ({ ...defaultFilters }));
  // ensure we only attempt to render Leaflet-dependent component on client
  const [isClient, setIsClient] = useState(false);
  useEffect(() => {
    setIsClient(true);
  }, []);

  // robust platform matcher (defensive)
  const platformMatches = useCallback((communityPlatforms = [], filterPlatform) => {
    if (!filterPlatform || filterPlatform === "all") return true;
    return communityPlatforms.some((p) => {
      const val = (p?.type ?? p?.name ?? p?.slug ?? p)?.toString().toLowerCase();
      return val === filterPlatform.toString().toLowerCase();
    });
  }, []);

  const filteredCommunities = useMemo(() => {
    return communities.filter((community) => {
      if (filters.guild !== "all" && community.guildId !== filters.guild) return false;
      if (filters.platform !== "all" && !platformMatches(community.platforms, filters.platform))
        return false;
      if (filters.region !== "all" && community.region !== filters.region) return false;
      return true;
    });
  }, [filters, platformMatches]);

  const updateFilter = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters({ ...defaultFilters });
  }, []);

  // KEY: explicit height for the map container. If your layout sets height elsewhere,
  // adjust this value or keep it in CSS. Leaflet requires a non-zero height.
  const mapContainerStyle = {
    height: "600px", // <-- adjust to taste or make responsive
    minHeight: "300px",
    width: "100%"
  };

  // key to force remount when filters or visible count change (helps with Leaflet init)
  const mapKey = `${filteredCommunities.length}-${filters.guild}-${filters.platform}-${filters.region}`;

  return (
    <div className="global-map-full-bleed">
      <section className="global-map-hero">
        <div className="space-y-2">
          <p className="eyebrow text-brand-steel">Global map</p>
          <h1 className="text-4xl text-slate-100">Live overview of every verified community</h1>
          <p className="text-sm text-slate-400 sm:text-base">
            Use a few precise filters to focus the map on the guilds and regions that matter most.
          </p>
        </div>

        <div className="global-map-stat-row">
          {[...mapStats, { label: "Communities visible", value: filteredCommunities.length }].map((stat) => (
            <div key={stat.label} className="global-map-stat-card">
              <p className="text-xs uppercase tracking-[0.3em] text-brand-steel">{stat.label}</p>
              <p className="text-3xl font-semibold text-slate-100">{stat.value}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="global-map-layout">
        <aside className="global-map-filters" aria-label="Map filters">
          <p className="text-xs uppercase tracking-[0.3em] text-brand-steel">Filters</p>
          <div className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="filter-guild" className="text-xs uppercase tracking-[0.3em] text-slate-500">Guild</label>
              <select
                id="filter-guild"
                value={filters.guild}
                onChange={(event) => updateFilter("guild", event.target.value)}
                className="filter-input"
              >
                <option value="all">All guilds</option>
                {guilds.map((guild) => (
                  <option key={guild.id} value={guild.id}>
                    {guild.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label htmlFor="filter-platform" className="text-xs uppercase tracking-[0.3em] text-slate-500">Platform</label>
              <select
                id="filter-platform"
                value={filters.platform}
                onChange={(event) => updateFilter("platform", event.target.value)}
                className="filter-input"
              >
                <option value="all">All platforms</option>
                {communityFilters.platforms.map((platform) => (
                  <option key={platform} value={platform}>
                    {platform}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label htmlFor="filter-region" className="text-xs uppercase tracking-[0.3em] text-slate-500">Region</label>
              <select
                id="filter-region"
                value={filters.region}
                onChange={(event) => updateFilter("region", event.target.value)}
                className="filter-input"
              >
                <option value="all">All regions</option>
                {communityFilters.regions.map((region) => (
                  <option key={region} value={region}>
                    {region}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-3">
              <button type="button" onClick={resetFilters} className="global-filter-reset">
                Clear filters
              </button>
            </div>
          </div>

        </aside>

        <div className="global-map-stage">
          <div className="global-map-stage__canvas">
            {/* Important: only render the Leaflet-dependent component on client, and give it a stable key so it remounts when relevant */}
            {isClient ? (
              <div style={mapContainerStyle} aria-hidden={false}>
                <CommunityMap
                  key={mapKey}
                  communities={filteredCommunities}
                  height="100%" // your CommunityMap can still accept height; container has explicit pixel height
                />
              </div>
            ) : (
              // server-render friendly placeholder to avoid hydration issues
              <div style={{ height: mapContainerStyle.height, minHeight: mapContainerStyle.minHeight, width: "100%", background: "transparent" }} />
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default GlobalMap;
